import { observer } from 'mobx-react-lite';
import { PopulatedExperienceTag } from '@/schemas/experience-tag-schema.ts';
import { useEffect, useState } from 'react';
import SearchBar from '@components/core/search-bar/search-bar.tsx';
import ExperienceTable from '@components/tables/experience-table.tsx';
import { CustomCellRendererProps } from 'ag-grid-react';
import { FullExperience } from '@/schemas/experience-schema.ts';
import OptionsMenu from '@components/core/options-menu/options-menu.tsx';
import modalStore from '@store/modal-store.ts';
import ExperienceTagDetailsRemoveExperienceModal from '@views/experience-tag-details/view-components/experience-tag-details-remove-experience-modal.tsx';
import copyService from '@services/copy-service.ts';
import searchService from '@services/search-service.ts';
import { useDebounce } from '@uidotdev/usehooks';

const robloxPlaceUrl = 'https://www.roblox.com/games/';

interface ExperienceTagDetailsExperiencesProps {
	tag: PopulatedExperienceTag;
}

const ExperienceTagDetailsExperiences = observer(
	function ExperienceTagDetailsExperiences(
		props: ExperienceTagDetailsExperiencesProps
	) {
		const [experiences, setExperiences] = useState(props.tag.places);

		const [search, setSearch] = useState('');
		const searchDebounce = useDebounce(search, 300);

		useEffect(() => {
			if (searchDebounce.length) {
				setExperiences(
					[...props.tag.places].filter((exp) =>
						searchService.experience(exp, searchDebounce)
					)
				);
			} else {
				setExperiences([...props.tag.places]);
			}
		}, [props.tag, searchDebounce]);

		return (
			<div className={'flex flex-col justify-center w-full gap-4 pt-1'}>
				<div className={'px-4'}>
					<SearchBar
						onSearchUpdate={setSearch}
						placeholder={'Search...'}
					/>
				</div>

				<ExperienceTable
					experiences={experiences}
					contextMenu={{
						cellRenderer: (params: CustomCellRendererProps) => {
							const experience = params.node
								.data as FullExperience;
							return (
								<OptionsMenu
									vertical
									buttons={[
										{
											title: 'View on roblox.com',
											onClick: () => {
												window.open(
													`${robloxPlaceUrl}${experience.place_id}`
												);
											},
										},
										{
											title: 'Remove',
											onClick: () => {
												modalStore.open(
													<ExperienceTagDetailsRemoveExperienceModal
														tag={props.tag}
														experience={experience}
													/>
												);
											},
										},
										{
											title: 'Copy Experience ID',
											onClick: () => {
												copyService.experienceId(
													experience
												);
											},
										},
										{
											title: 'Copy Universe ID',
											onClick: () => {
												copyService.experienceUniverseId(
													experience
												);
											},
										},
									]}
									width={'13rem'}
								/>
							);
						},
						minWidth: 50,
						maxWidth: 50,
						resizable: false,
						sortable: false,
						cellStyle: {
							display: 'flex',
							justifyContent: 'flex-end',
							alignItems: 'center',
						},
						context: {
							priority: 1,
						},
					}}
					fillParent
					includeRanking={false}
					emptyMessage={
						'Let’s get things rolling! Add an experience to unlock stats and insights.'
					}
					staticPageSize={20}
				/>
			</div>
		);
	}
);

export default ExperienceTagDetailsExperiences;
